import React, { useState, useCallback } from 'react';
import {
  Folder,
  FolderOpen,
  FileCode,
  FileJson,
  FileText,
  File,
  FilePlus,
  FolderPlus,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { useEditorStore, useWorkspaceStore } from '@/store';
import * as fileService from '@/services/file.service';
import ContextMenu from './ContextMenu';
import { cn } from '@/lib/utils';
import type { WorkspaceFile } from '@/types';

function getFileIcon(name: string) {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'ts':
    case 'tsx':
    case 'js':
    case 'jsx':
    case 'sol':
      return <FileCode className="w-4 h-4 text-blue-400" />;
    case 'json':
      return <FileJson className="w-4 h-4 text-yellow-400" />;
    case 'md':
    case 'txt':
      return <FileText className="w-4 h-4 text-gray-400" />;
    default:
      return <File className="w-4 h-4 text-gray-400" />;
  }
}

interface TreeNodeProps {
  node: WorkspaceFile;
  depth: number;
  workspaceId: string;
  onContextMenu: (e: React.MouseEvent, node: WorkspaceFile) => void;
}

function TreeNode({ node, depth, workspaceId, onContextMenu }: TreeNodeProps) {
  const [isOpen, setIsOpen] = useState(depth < 1);
  const { openFile, openFiles } = useEditorStore();

  const handleClick = useCallback(async () => {
    if (node.type === 'directory') {
      setIsOpen((v) => !v);
      return;
    }

    // Check if content is already loaded
    const cached = openFiles.get(node.path);
    if (cached !== undefined) {
      openFile(node.path, cached, node.language);
      return;
    }

    try {
      const content = await fileService.getFileContent(workspaceId, node.path);
      openFile(node.path, content, node.language);
    } catch (err) {
      console.error('Failed to load file:', err);
    }
  }, [node, workspaceId, openFile, openFiles]);

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-1 px-2 py-0.5 cursor-pointer hover:bg-[#2a2d2e] text-sm text-gray-300 select-none'
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={handleClick}
        onContextMenu={(e) => onContextMenu(e, node)}
      >
        {node.type === 'directory' ? (
          <>
            {isOpen ? (
              <ChevronDown className="w-3.5 h-3.5 text-gray-500 shrink-0" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-gray-500 shrink-0" />
            )}
            {isOpen ? (
              <FolderOpen className="w-4 h-4 text-yellow-500 shrink-0" />
            ) : (
              <Folder className="w-4 h-4 text-yellow-500 shrink-0" />
            )}
          </>
        ) : (
          <>
            <span className="w-3.5 shrink-0" />
            {getFileIcon(node.name)}
          </>
        )}
        <span className="truncate">{node.name}</span>
      </div>

      {node.type === 'directory' && isOpen && node.children && (
        <div className="transition-all duration-150">
          {node.children
            .slice()
            .sort((a, b) => {
              if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
              return a.name.localeCompare(b.name);
            })
            .map((child) => (
              <TreeNode
                key={child.path}
                node={child}
                depth={depth + 1}
                workspaceId={workspaceId}
                onContextMenu={onContextMenu}
              />
            ))}
        </div>
      )}
    </div>
  );
}

interface FileExplorerProps {
  workspaceId: string;
}

export default function FileExplorer({ workspaceId }: FileExplorerProps) {
  const { fileTree, setFileTree } = useEditorStore();
  const { activeWorkspace } = useWorkspaceStore();
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    node: WorkspaceFile;
  } | null>(null);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, node: WorkspaceFile) => {
      e.preventDefault();
      setContextMenu({ x: e.clientX, y: e.clientY, node });
    },
    []
  );

  const refreshTree = useCallback(async () => {
    try {
      const tree = await fileService.getFileTree(workspaceId);
      const root: WorkspaceFile = {
        path: '/',
        name: activeWorkspace?.name ?? 'workspace',
        type: 'directory',
        children: tree,
      };
      setFileTree(root);
    } catch (err) {
      console.error('Failed to refresh file tree:', err);
    }
  }, [workspaceId, activeWorkspace, setFileTree]);

  const handleNewFile = useCallback(async () => {
    if (!contextMenu) return;
    const parentPath =
      contextMenu.node.type === 'directory'
        ? contextMenu.node.path
        : contextMenu.node.path.split('/').slice(0, -1).join('/');
    const name = prompt('File name:');
    if (!name) return;
    const fullPath = `${parentPath}/${name}`.replace(/\/+/g, '/');
    try {
      await fileService.createFile(workspaceId, fullPath, 'file');
      await refreshTree();
    } catch (err) {
      console.error('Failed to create file:', err);
    }
  }, [contextMenu, workspaceId, refreshTree]);

  const handleNewFolder = useCallback(async () => {
    if (!contextMenu) return;
    const parentPath =
      contextMenu.node.type === 'directory'
        ? contextMenu.node.path
        : contextMenu.node.path.split('/').slice(0, -1).join('/');
    const name = prompt('Folder name:');
    if (!name) return;
    const fullPath = `${parentPath}/${name}`.replace(/\/+/g, '/');
    try {
      await fileService.createFile(workspaceId, fullPath, 'directory');
      await refreshTree();
    } catch (err) {
      console.error('Failed to create folder:', err);
    }
  }, [contextMenu, workspaceId, refreshTree]);

  const handleRename = useCallback(async () => {
    if (!contextMenu) return;
    const newName = prompt('New name:', contextMenu.node.name);
    if (!newName || newName === contextMenu.node.name) return;
    const parentPath = contextMenu.node.path.split('/').slice(0, -1).join('/');
    const newPath = `${parentPath}/${newName}`.replace(/\/+/g, '/');
    try {
      await fileService.renameFile(workspaceId, contextMenu.node.path, newPath);
      await refreshTree();
    } catch (err) {
      console.error('Failed to rename:', err);
    }
  }, [contextMenu, workspaceId, refreshTree]);

  const handleDelete = useCallback(async () => {
    if (!contextMenu) return;
    if (!confirm(`Delete "${contextMenu.node.name}"?`)) return;
    try {
      await fileService.deleteFile(workspaceId, contextMenu.node.path);
      await refreshTree();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  }, [contextMenu, workspaceId, refreshTree]);

  return (
    <div className="h-full flex flex-col bg-[#252526] text-gray-300 select-none">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#3c3c3c]">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          Explorer
        </span>
        <div className="flex gap-1">
          <button
            className="p-1 rounded hover:bg-[#3c3c3c] text-gray-400 hover:text-gray-200"
            title="New File"
            onClick={() => {
              if (!fileTree) return;
              setContextMenu({ x: 0, y: 0, node: fileTree });
              handleNewFile();
            }}
          >
            <FilePlus className="w-4 h-4" />
          </button>
          <button
            className="p-1 rounded hover:bg-[#3c3c3c] text-gray-400 hover:text-gray-200"
            title="New Folder"
            onClick={() => {
              if (!fileTree) return;
              setContextMenu({ x: 0, y: 0, node: fileTree });
              handleNewFolder();
            }}
          >
            <FolderPlus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Workspace name */}
      {activeWorkspace && (
        <div className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-[#2a2d2e] border-b border-[#3c3c3c]">
          {activeWorkspace.name}
        </div>
      )}

      {/* Tree */}
      <div className="flex-1 overflow-y-auto py-1">
        {fileTree?.children?.map((node) => (
          <TreeNode
            key={node.path}
            node={node}
            depth={0}
            workspaceId={workspaceId}
            onContextMenu={handleContextMenu}
          />
        ))}
        {!fileTree && (
          <div className="px-3 py-4 text-xs text-gray-500 text-center">
            No files loaded
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && contextMenu.x > 0 && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onNewFile={handleNewFile}
          onNewFolder={handleNewFolder}
          onRename={handleRename}
          onDelete={handleDelete}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
}
